import React, { useState, useEffect } from "react";
import { MainLayout } from "../../layout/MainLayout";
import { Grid } from "../../UI/Grid";
import { Box } from "../../UI/Box";
import { Typography } from "../../UI/Typography";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useNetwork } from "../../hooks/useNetwork";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";
import { URL as NavigationURL } from "../../constants/navigation";
import useAuth from "../login/AuthContext";

import { Step, MatchingRound, PreviousExport, GroupStatistics } from "./types";
import { sendBackRoommates, getPreviousExports, downloadExportCsv, ExportRun } from "../../services/IntegrationApi";
import { GetAllNetworkInfo } from "../../services/NetworkApi";
import { downloadBlob } from "../../helpers/downloadBlob";

import {
  PageHeader,
  StepperHeader,
  SelectMatchingRoundStep,
  ExportSummaryStep,
  ReviewStep,
  ConfirmExportStep,
  ExportLogStep,
  NoMatchingRounds,
  ArchivedExports
} from "./components";

export const SendToEntrataPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>(Step.SELECT_MATCHING_ROUND);
  const [selectedMatchingRound, setSelectedMatchingRound] = useState<number | null>(null);
  const [matchingRounds, setMatchingRounds] = useState<MatchingRound[]>([]);
  const [archivedMatchingRounds, setArchivedMatchingRounds] = useState<MatchingRound[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasEligibleRounds, setHasEligibleRounds] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [previousExports, setPreviousExports] = useState<PreviousExport[]>([]);
  const [latestExportRunId, setLatestExportRunId] = useState<string | null>(null);
  const [selectedExport, setSelectedExport] = useState<PreviousExport | null>(null);
  const [isFromArchivedExport, setIsFromArchivedExport] = useState(false);
  const [groupStatistics, setGroupStatistics] = useState<GroupStatistics>({
    fullGroups: 0,
    partialGroups: 0,
    singleResidents: 0
  });

  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const { selectedNetwork } = useNetwork();
  const { isEntrata } = useSelectedNetworkImportTypes();
  const { getAuthToken, isSuperAdmin } = useAuth();

  // Utility function to convert date array to readable format
  const formatDateFromArray = (dateArray: number[]): { date: string; time: string; compact: string } => {
    if (!dateArray || dateArray.length < 6) {
      return { date: 'Unknown', time: 'Unknown', compact: 'Unknown' };
    }

    const [year, month, day, hour, minute] = dateArray;
    const date = new Date(year, month - 1, day, hour, minute); // month is 0-indexed in JS Date

    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // Create compact format like "7/29/2025 4:25pm"
    const compactDate = `${month}/${day}/${year}`;
    const compactTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase();
    const compact = `${compactDate} ${compactTime}`;

    return { date: dateStr, time: timeStr, compact };
  };

  // Convert API response to component format
  const convertRunsToPreviousExports = (runs: ExportRun[]): PreviousExport[] => {
    return runs.map((run, index) => {
      const startDateTime = formatDateFromArray(run.startTime);

      

      return {
        id: index + 1, // Use index as ID since API doesn't provide numeric ID
        runId: run.id, // Preserve the run ID for CSV download
        matchingRoundName: '', // Not provided by API
        date: startDateTime.date,
        exportDate: startDateTime.compact, // Use compact format for display
        exportTime: startDateTime.time,
        successfulGroups: run.numberOfSuccesses,
        unsuccessfulGroups: run.numberOfFailures,
        singleResidents: run.singleRoommates,
        status: run.status
      };
    });
  };

  // Load matching rounds when the component mounts or when the selected network changes
  useEffect(() => {
    const fetchMatchingRounds = async () => {
      // Don't fetch if no network is selected
      if (!selectedNetwork.l1Id) {
        return;
      }

      try {
        const data = await GetAllNetworkInfo([selectedNetwork.l1Id], await getAuthToken());

        // Extract networks from the response
        const networks = data.networks || [];

        // Transform Network data to MatchingRound format
        const rounds: MatchingRound[] = networks.map((network) => ({
          id: network.l2Id,
          name: network.l2Name,
          status: network.matchingRoundStatus || "Unknown",
          date: network.matchingStartDate || ""
        }));

        // Filter to only include eligible rounds for export (CLOSED or ACTIVE)
        // Note: "ACTIVE POST MATCHING" is interpreted as "ACTIVE" status
        const eligibleRounds = rounds.filter(round =>
          round.status === "CLOSED" || round.status === "ACTIVE"
        );

        // Filter archived rounds (all rounds that are not eligible for new exports)
        const archivedRounds = rounds.filter(round =>
          round.status !== "CLOSED" && round.status !== "ACTIVE" && round.status !== "Unknown"
        ).map(round => ({ ...round, hasExports: false })); // Initialize as no exports

        setMatchingRounds(eligibleRounds);
        setHasEligibleRounds(eligibleRounds.length > 0);
        setArchivedMatchingRounds(archivedRounds); // Set immediately with hasExports: false

        // Fetch export counts for archived rounds and update
        if (archivedRounds.length > 0) {
          const authToken = await getAuthToken();
          const archivedRoundsWithExports = await Promise.all(
            archivedRounds.map(async (round) => {
              try {
                const response = await getPreviousExports(
                  selectedNetwork.l1Id,
                  round.id,
                  authToken
                );
                const hasExportsResult = !!(response?.runs?.length && response.runs.length > 0);
                return {
                  ...round,
                  hasExports: hasExportsResult
                };
              } catch {
                return { ...round, hasExports: false };
              }
            })
          );
          setArchivedMatchingRounds(archivedRoundsWithExports);
        }
      } catch (error) {
        // console.error('Error fetching matching rounds:', error);
        showErrorSnackbar("Failed to load matching rounds");
        setHasEligibleRounds(false);
      }
    };

    fetchMatchingRounds();
  }, [selectedNetwork.l1Id]);

  // Load groups when a matching round is selected
  useEffect(() => {
    if (selectedMatchingRound) {
      const fetchGroups = async () => {
        try {
          setIsLoading(true);
          // Simulate API delay
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        } catch (error) {
          showErrorSnackbar("Failed to load groups");
          setIsLoading(false);
        }
      };

      fetchGroups();
    }
  }, [selectedMatchingRound, showErrorSnackbar]);

  // Function to fetch previous exports for a specific matching round
  const fetchPreviousExports = async (l2Id: number) => {
    if (!selectedNetwork.l1Id) {
      return;
    }

    try {
      const response = await getPreviousExports(
        selectedNetwork.l1Id,
        l2Id,
        await getAuthToken()
      );

      if (response && response.runs) {
        const convertedExports = convertRunsToPreviousExports(response.runs);
        setPreviousExports(convertedExports);
      } else {
        setPreviousExports([]);
      }
    } catch (error) {
      showErrorSnackbar("Failed to load previous exports");
      setPreviousExports([]);
    }
  };

  const handleMatchingRoundChange = (event: any) => {
    const roundId = event.target.value as number;
    setSelectedMatchingRound(roundId);

    // Go directly to review step when a matching round is selected
    if (roundId) {
      fetchPreviousExports(roundId);
      setCurrentStep(Step.REVIEW);
    }
  };

  const handleSelectArchivedRound = async (roundId: number) => {
    setSelectedMatchingRound(roundId);

    // Fetch previous exports for the archived matching round and go to export summary
    if (roundId && selectedNetwork.l1Id) {
      try {
        const response = await getPreviousExports(
          selectedNetwork.l1Id,
          roundId,
          await getAuthToken()
        );

        if (response && response.runs && response.runs.length > 0) {
          const convertedExports = convertRunsToPreviousExports(response.runs);
          setPreviousExports(convertedExports);
          // Auto-select the latest export (last in the list)
          const latestExport = convertedExports[convertedExports.length - 1];
          setSelectedExport(latestExport);
          setIsFromArchivedExport(true); // Mark that we came from archived exports
          setCurrentStep(Step.EXPORT_SUMMARY);
        } else {
          setPreviousExports([]);
        }
      } catch (error) {
        showErrorSnackbar("Failed to load previous exports");
        setPreviousExports([]);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === Step.EXPORT_LOG) {
      // From the last screen (Export Log), go back to the very first screen
      setCurrentStep(Step.SELECT_MATCHING_ROUND);
    } else if (currentStep === Step.EXPORT_SUMMARY) {
      setCurrentStep(Step.SELECT_MATCHING_ROUND);
      setSelectedExport(null);
    } else if (currentStep === Step.REVIEW) {
      setCurrentStep(Step.SELECT_MATCHING_ROUND);
    } else {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const handleViewMatchingRounds = () => {
    // Navigate to matching rounds page
    window.location.href = NavigationURL.MATCH_RULES;
  };

  const toggleArchived = () => {
    setShowArchived(!showArchived);
  };

  const handleDownloadCsv = async () => {
    if (!selectedNetwork.l1Id || !selectedMatchingRound || !latestExportRunId) {
      showErrorSnackbar("Missing export information for CSV download");
      return;
    }

    try {
      const response = await downloadExportCsv(
        selectedNetwork.l1Id,
        selectedMatchingRound,
        latestExportRunId,
        await getAuthToken()
      );

      downloadBlob(response.blob, response.fileName || `export-${latestExportRunId}.csv`);
      showSuccessSnackbar("CSV file downloaded successfully");
    } catch (error) {
      showErrorSnackbar("Failed to download CSV file");
    }
  };

  const handleDownloadCsvByRunId = async (runId: string) => {
    if (!selectedNetwork.l1Id || !selectedMatchingRound) {
      showErrorSnackbar("Missing export information for CSV download");
      return;
    }

    try {
      const response = await downloadExportCsv(
        selectedNetwork.l1Id,
        selectedMatchingRound,
        runId,
        await getAuthToken()
      );

      downloadBlob(response.blob, response.fileName || `export-${runId}.csv`);
      showSuccessSnackbar("CSV file downloaded successfully");
    } catch (error) {
      showErrorSnackbar("Failed to download CSV file");
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      if (!selectedMatchingRound || !selectedNetwork.l1Id) {
        showErrorSnackbar("Please select a matching round");
        setIsLoading(false);
        return;
      }

      // Call the new sendBackRoommates endpoint
      const sendBackResponse = await sendBackRoommates(
        selectedNetwork.l1Id,
        selectedMatchingRound,
        { matchingRoundId: selectedMatchingRound },
        await getAuthToken()
      );

      // Check if the API call was successful
      if (sendBackResponse.status !== 200) {
        // Extract error message from the response
        let errorMessage = "An error occurred while sending roommates to Entrata";

        if (sendBackResponse.parsedBody && typeof sendBackResponse.parsedBody === 'object') {
          const errorBody = sendBackResponse.parsedBody as any;
          if (errorBody.error) {
            if (errorBody.error.description) {
              errorMessage = errorBody.error.description;
            } else if (errorBody.error.code) {
              errorMessage = errorBody.error.code;
            }
          }
        }

        showErrorSnackbar(errorMessage);
        setIsLoading(false);
        return;
      }

      // Refresh previous exports to get the latest export runId
      try {
        const response = await getPreviousExports(
          selectedNetwork.l1Id,
          selectedMatchingRound,
          await getAuthToken()
        );

        if (response && response.runs && response.runs.length > 0) {
          // Get the last export (last element in the list)
          const latestRun = response.runs[response.runs.length - 1];
          setLatestExportRunId(latestRun.id);

          // Convert the latest run to PreviousExport format and set as selectedExport
          const latestExportAsPreviousExport = convertRunsToPreviousExports([latestRun])[0];
          setSelectedExport(latestExportAsPreviousExport);

          // Also update the previousExports list to include the new export
          const allConvertedExports = convertRunsToPreviousExports(response.runs);
          setPreviousExports(allConvertedExports);
        }
      } catch (exportError) {
        // console.warn("Failed to fetch latest export runId:", exportError);
      }

      showSuccessSnackbar("Successfully sent roommates back to Entrata");
      setIsFromArchivedExport(false); // Mark that we came from the export flow
      setCurrentStep(Step.EXPORT_SUMMARY);
      setIsLoading(false);
    } catch (error) {
      showErrorSnackbar("An error occurred while sending roommates to Entrata");
      setIsLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case Step.SELECT_MATCHING_ROUND:
        return (
          <SelectMatchingRoundStep
            matchingRounds={matchingRounds}
            selectedMatchingRound={selectedMatchingRound}
            handleMatchingRoundChange={handleMatchingRoundChange}
          />
        );

      case Step.EXPORT_SUMMARY:
        return (
          <ExportSummaryStep
            selectedExport={selectedExport}
            onDownloadCsv={handleDownloadCsvByRunId}
            handleBack={handleBack}
            setCurrentStep={setCurrentStep}
            isFromArchivedExport={isFromArchivedExport}
          />
        );

      case Step.REVIEW:
        return (
          <ReviewStep
            setCurrentStep={setCurrentStep}
            selectedMatchingRound={selectedMatchingRound}
            selectedNetworkL1Id={selectedNetwork.l1Id}
            onGroupStatisticsUpdate={setGroupStatistics}
          />
        );

      case Step.CONFIRM_EXPORT:
        return (
          <ConfirmExportStep
            handleBack={handleBack}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            isSuperAdmin={isSuperAdmin()}
            hasPreviousExports={previousExports.length > 0}
            groupStatistics={groupStatistics}
            previousExports={previousExports}
            onDownloadCsv={handleDownloadCsvByRunId}
          />
        );

      case Step.EXPORT_LOG:
        return <ExportLogStep onDownloadCsv={handleDownloadCsv} setCurrentStep={setCurrentStep} />;

      default:
        return null;
    }
  };

  // Check if the current network has Entrata integration
  if (!isEntrata) {
    return (
      <MainLayout title="Send To Entrata">
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              textAlign: 'center',
              p: 4
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Access Restricted
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
                The "Send to Entrata" feature is only available for networks with Entrata integration.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Send To Entrata">
      <Grid container>
        <PageHeader currentStep={currentStep} />
        <StepperHeader
          currentStep={currentStep}
          handleBack={handleBack}
          selectedMatchingRoundName={
            matchingRounds.find((r: MatchingRound) => r.id === selectedMatchingRound)?.name ||
            archivedMatchingRounds.find((r: MatchingRound) => r.id === selectedMatchingRound)?.name
          }
        />

        {hasEligibleRounds ? (
          renderCurrentStep()
        ) : (
          <NoMatchingRounds handleViewMatchingRounds={handleViewMatchingRounds} />
        )}

        {(currentStep === Step.SELECT_MATCHING_ROUND || currentStep === Step.REVIEW) && (
          <Grid item xs={12}>
            <ArchivedExports
              showArchived={showArchived}
              toggleArchived={toggleArchived}
              archivedMatchingRounds={archivedMatchingRounds}
              onSelectArchivedRound={handleSelectArchivedRound}
            />
          </Grid>
        )}
      </Grid>
    </MainLayout>
  );
};

export const SEND_TO_ENTRATA_URL = "/users/send-to-entrata";
